import * as React from 'react';
import API from "../../common/API";
import { useAuth } from "react-oidc-context";
import { LinkContainer } from 'react-router-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UserCourseDTO, CourseStatus, CourseMetadata } from "../../common/TrainingTypes";
import { usePagination } from '../../hooks/usePagination';
import { useTranslation } from 'react-i18next';

export function CourseListComponent() {
    const auth = useAuth();
    const { t } = useTranslation()

    const [courses, pagination, setCustomParams] = usePagination<UserCourseDTO>({ perPage: 10, route: '/api/v1/training/courses' })

    // Filters
    const [search, setSearch] = React.useState<string>()
    const [filter, setFilter] = React.useState<string>()
    const [category, setCategory] = React.useState<string>()
    const [categories, setCategories] = React.useState<string[]>([])
    const [tag, setTag] = React.useState<string>()
    const [status, setStatus] = React.useState<CourseStatus|null>(CourseStatus.New)
    const [tags, setTags] = React.useState<string[]>([])

    React.useEffect(() => {
        const getCourseMetadata = async () => {
            const courseMetadata: CourseMetadata | null = await API.get("/api/v1/training/courses/metadata", auth)
            if (courseMetadata == null)
                return;
            setCategories(courseMetadata.categories)
            setTags(courseMetadata.tags)
        }
        getCourseMetadata()
    }, [auth])

    React.useEffect(() => {
        const params: URLSearchParams = new URLSearchParams()
        if (filter != null)
            params.set("filter", filter);
        if (category != null)
            params.set("category", category);
        if (tag != null)
            params.set("tag", tag);
        if (status != null)
            params.set("status", status.toString());
        setCustomParams(params.toString());
    }, [category, tag, filter, status, setCustomParams])

    const searchSubmit = (ev: React.FormEvent) => {
        if (search !== undefined && search.length > 0)
            setFilter('Name.Contains("' + search + '")');
        else
            setFilter(undefined);

        ev.preventDefault();

        return false;
    }

	return (
        <div className="p-0 container-fluid row">
            <h1 className="mb-3 d-inline col-md-6">
                <FontAwesomeIcon icon="graduation-cap" className="me-2" />
                {t('training:training')}
            </h1>
            <div className="col-xl-3 col-xxl-4 my-2 d-flex justify-content-end">
                <div className="dropdown">
                    <button className={"btn btn-outline-primary dropdown-toggle me-2 "} type="button" data-bs-toggle="dropdown">
                        <FontAwesomeIcon icon="info" className="me-1"></FontAwesomeIcon>
                        {status != null && status === CourseStatus.New ? t('training:available') :
                         status != null && status === CourseStatus.Pass ? t('training:passed') : t('training:status')}
                    </button>
                    <ul className="dropdown-menu">
                        <li><button type="button" className="dropdown-item" onClick={function () { setStatus(null) }}>{t('training:all')}</button></li>
                        <li><button type="button" className="dropdown-item" onClick={function () { setStatus(CourseStatus.New) }}>{t('training:available')}</button></li>
                        <li><button type="button" className="dropdown-item" onClick={function () { setStatus(CourseStatus.Pass) }}>{t('training:passed')}</button></li>
                    </ul>
                </div>
                <div className="dropdown">
                    <button className={"btn btn-outline-primary dropdown-toggle me-2 "} type="button" data-bs-toggle="dropdown">
                        <FontAwesomeIcon icon="tags" className="me-1"></FontAwesomeIcon>
                        {tag ? tag : t('training:tags')}
                    </button>
                    <ul className="dropdown-menu">
                        <li><button type="button" className="dropdown-item" onClick={function () { setTag(undefined) }}>{t('training:all')}</button></li>
                        {tags?.map((tag: string) => {
                            return (
                                <li key={tag}><button className="dropdown-item" type="button" onClick={function () { setTag(tag) }}>{tag}</button></li>
                            )
                        })}
                    </ul>
                </div>
                <div className="dropdown">
                    <button className="btn btn-outline-primary dropdown-toggle" type="button" data-bs-toggle="dropdown">
                        <FontAwesomeIcon icon="tasks" className="me-1"></FontAwesomeIcon>
                        {category ? category : t('training:categories')}
                    </button>
                    <ul className="dropdown-menu">
                        <li><button type="button" className="dropdown-item" onClick={function () { setCategory(undefined) } }>All</button></li>
                        {categories?.map((category: string) => {
                            return (
                                <li key={category}><button className="dropdown-item" type="button" onClick={function () { setCategory(category) }}>{category}</button></li>
                            )
                        })}
                    </ul>
                </div>
            </div>
            <div className="col-xl-3 col-xxl-2 my-2">
                <form onSubmit={searchSubmit} className="input-group">
                    <input type="text" className="form-control" style={{ minWidth: '150px' }} placeholder="Search" onChange={(ev) => setSearch(ev.target.value)} />
                    <button className="btn btn-primary" type="submit"><FontAwesomeIcon icon="search" /></button>
                </form>
            </div>

			{courses?.map((course: UserCourseDTO) => {
				return (
                    <div className="col-12 col-md-6 col-lg-4 col-xl-3 col-xxl-2" key={course.id}>
                        <LinkContainer to={"/training/course/" + course.id}>
                            <div className="card">
                                {course.thumbnail !== null && course.thumbnail.length > 0 ?
                                    <img className="card-img-top" style={{cursor: 'pointer'}} src={course.thumbnail} alt="Course"></img> : <></>}
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <h5 className="card-title mb-0" style={{ cursor: 'pointer', fontWeight: '700' }}>{course.name}</h5>
                                    {!course.prerequisiteComplete ? <div className="badge my-2 bg-danger">{t('training:locked')}</div> :
                                        course.status === CourseStatus.Pass ? <div className="badge my-2 bg-success">{course.grade}%</div> :
                                        course.status === CourseStatus.Expired ? <div className="badge my-2 bg-warning">{t('training:expired')}</div> :
                                        course.status === CourseStatus.InProgress ? <div className="badge my-2 bg-info">{t('training:inProgress')}</div> :
                                        <div className="badge my-2 bg-primary">{t('training:new')}</div>
                                    }
							    </div>
                                <div className="card-body text-center pt-0">
                                    <span><FontAwesomeIcon icon="tasks" className="me-1"></FontAwesomeIcon>{course.category}</span>
                                    <div className="pb-2">
                                        <FontAwesomeIcon icon="tags" className="me-1"></FontAwesomeIcon>
                                        {course.tags?.map((tag: string) => {
                                            return (
                                                <span key={tag} className="badge bg-primary me-2">{tag}</span>
                                            )
                                        })}
                                    </div>
								    <p className="mb-0">{course.description}</p>
                                    {!course.prerequisiteComplete ? <div className="alert alert-warning alert-outline-coloured mb-0">
                                        <div className="alert-icon d-flex align-items-center"><FontAwesomeIcon icon="info-circle"></FontAwesomeIcon></div>
                                        <div className="alert-message">
                                            <strong>{t('training:prerequisiteIncomplete')}</strong>
                                            <div>{course.prerequisiteName}</div>
                                        </div>
                                    </div> : <></>}
							    </div>
                            </div>
                        </LinkContainer>
					</div>
				)
            })}

            <div className="mt-2">{pagination()}</div>
		</div>
	)
}